import { Input, Select, FormCard, FormItem } from '@lmc/ui-kit'
import { NAMESPACE } from 'locale'
import { serviceUserProfileConstants as supConstants } from 'locale/serviceUserProfile'
import { useTranslation } from 'react-i18next'
import {
  MARITAL_STATUSES,
  NATIONALITIES,
  LANGUAGES,
  religionsOptions,
  newEthnicities
} from 'common/constants'
import { Text } from '@lmc/ui-kit/src/general'
import { dateFormat, getSelectOptions } from '../../../utils/utils'
import styled from 'styled-components'
import { SupDatePicker } from '../../../styledComponents'
import { Gender } from '@lmc/ts-common'
import { Tooltip } from 'antd'
import useFeatureFlags from 'hooks/useFeatureFlags'
import dayjs from 'dayjs'

type DemographicFormCardProps = {
  residentAge: number | undefined
  isPro: boolean
}

const cutoffBirthdate = dayjs('01-01-1900', 'DD-MM-YYYY')
const disabledBirthdates = (birthdate: dayjs.Dayjs) => {
  const now = dayjs()
  return birthdate.isAfter(now) || birthdate.isBefore(cutoffBirthdate)
}

export const DemographicFormCard = ({
  residentAge,
  isPro
}: DemographicFormCardProps) => {
  const { t } = useTranslation(NAMESPACE.SERVICE_USER_PROFILE)
  const flags = useFeatureFlags({
    flagNames: ['prsb_resident_gender']
  })?.data
  const isPrsbGenderEnabled = flags?.prsb_resident_gender?.enabled

  const getGenderOptions = () => {
    const allowedGendersArray = [Gender.male, Gender.female]

    if (isPrsbGenderEnabled) {
      allowedGendersArray.push(Gender.unknown, Gender.unspecified, Gender.other)
    }

    const genderOptions = allowedGendersArray.map((gender) => ({
      value: gender,
      label: t(supConstants[gender])
    }))

    return genderOptions
  }

  const sortedReligions = religionsOptions.sort((a, b) =>
    a.label > b.label ? 1 : -1
  )

  return (
    <FormCard title={t(supConstants.DEMOGRAPHIC)}>
      <FormItem
        label={<Text>{t(supConstants.GENDER)}</Text>}
        name="gender"
        rules={[
          {
            required: true
          }
        ]}
      >
        <Select
          optionRender={(option) => <Text>{option.label}</Text>}
          options={getGenderOptions()}
        />
      </FormItem>
      <AgeContainer>
        <FormItem
          label={<Text>{t(supConstants.DATE_OF_BIRTH)}</Text>}
          name="dateOfBirth"
          rules={[
            {
              required: true,
              message: `${t(supConstants.PLEASE_ENTER)} ${t(
                supConstants.DATE_OF_BIRTH
              )}`
            }
          ]}
        >
          <SupDatePicker
            placeholder=""
            format={dateFormat}
            disabledDate={disabledBirthdates}
          />
        </FormItem>
        {residentAge && (
          <BaseText>
            {t(supConstants.AGE)} {residentAge}
          </BaseText>
        )}
      </AgeContainer>
      {isPro && (
        <>
          <FormItem
            label={<Text>{t(supConstants.MARITAL_STATUS)}</Text>}
            name="maritalStatus"
          >
            <Select options={maritalStatusOptions} />
          </FormItem>
          <FormItem
            label={<Text>{t(supConstants.BIRTHPLACE)}</Text>}
            name="birthplace"
          >
            <Input maxLength={50} />
          </FormItem>
          <FormItem
            label={<Text>{t(supConstants.NATIONALITY)}</Text>}
            name="nationalities"
          >
            <Select options={getSelectOptions(NATIONALITIES)} mode="tags" />
          </FormItem>
          <FormItem
            label={<Text>{t(supConstants.LANGUAGES_SPOKEN)}</Text>}
            name="languagesSpoken"
          >
            <Select options={getSelectOptions(LANGUAGES)} mode="tags" />
          </FormItem>
          <FormItem
            label={<Text>{t(supConstants.ETHNICITY)}</Text>}
            name="ethnicity"
          >
            <Select
              optionRender={(option) => <Text>{option.label}</Text>}
              options={newEthnicities}
            />
          </FormItem>
          <FormItem
            label={<Text>{t(supConstants.RELIGION)}</Text>}
            name="religion"
          >
            <Select
              showSearch
              options={sortedReligions}
              optionRender={(option) => (
                <Tooltip placement="top" title={option.label}>
                  <Text>{option.label}</Text>
                </Tooltip>
              )}
            />
          </FormItem>
        </>
      )}
    </FormCard>
  )
}

const maritalStatusOptions = MARITAL_STATUSES.map((maritalStatus) => ({
  value: maritalStatus.value,
  label: <Text>{maritalStatus.value}</Text>
}))

const AgeContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
`

const BaseText = styled(Text)`
  color: ${(props) => props.theme.color.neutral.color5};
`
